import { memo } from 'react';

import { Stack, Tooltip, Typography } from '@mui/material';
import { Clock } from 'lucide-react';

import {
  chipTypographyStyles,
  createChipStyle,
  useChipStatusColor,
} from '@modules/_shared/components/feed-v2/components/statusChipStyles';
import { tooltipProps } from '@modules/_shared/components/feed-v2/constants/styles';
import {
  formatDueDate,
  formatFullDate,
} from '@modules/_shared/components/feed-v2/utils/formatters';
import { useLocale } from '@shared/hooks/locale';

type Props = {
  due_at: string | null | undefined;
  start_date: string | null | undefined;
};

export const ActivityStatusChip = memo(({ due_at, start_date }: Props) => {
  const { t, locale } = useLocale();
  const dueDate = due_at || start_date;

  const dueResult = formatDueDate(locale, dueDate, t);
  const color = useChipStatusColor({
    status: dueResult.overdue ? 'error' : 'pending',
  });

  if (dueDate) {
    return (
      <Tooltip title={formatFullDate(locale, dueDate)} {...tooltipProps}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={[
            {
              alignItems: 'center',
            },
            createChipStyle(dueResult.overdue ? 'error' : 'pending'),
          ]}
        >
          <Clock size={12} color={color} />
          <Typography variant="caption" sx={{ color, ...chipTypographyStyles }}>
            {dueResult.text}
          </Typography>
        </Stack>
      </Tooltip>
    );
  }

  // No due date or start date - show pending chip
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={[
        {
          alignItems: 'center',
        },
        createChipStyle('pending'),
      ]}
    >
      <Clock size={12} color={color} />
      <Typography variant="caption" sx={{ color, ...chipTypographyStyles }}>
        {t('Pending')}
      </Typography>
    </Stack>
  );
});
